import React from "react";
import { Modal } from "react-bootstrap";
import { OPERATIONAL_DAYS, shipFieldDefs } from "./storerForm";

export const shipModalTitle = (entityLabel) => {
  if (entityLabel === "Customer") return "Ship To";
  if (entityLabel === "Supplier") return "Ship From";
  return "";
};

export const getShipFields = (entityLabel) => {
  if (entityLabel === "Customer") return shipFieldDefs("shipto", "Ship To");
  if (entityLabel === "Supplier") return shipFieldDefs("shipfrom", "Ship From");
  return null;
};

const optionValue = (opt) => (typeof opt === "string" ? opt : opt.value);
const optionLabel = (opt) => (typeof opt === "string" ? opt : opt.label);

export default function ShipAddressModal({
  show,
  title,
  fields = [],
  form = {},
  errors = {},
  onChange,
  onClose,
  onSave,
  readOnly = false,
  saving = false,
  showOperationalHours = false,
  operationalHours = {},
  onOperationalHoursChange,
}) {
  const setField = (name, value) => {
    if (typeof onChange === "function") onChange({ ...form, [name]: value });
  };

  const setDay = (day, enabled) => {
    if (typeof onOperationalHoursChange !== "function") return;
    onOperationalHoursChange({
      ...operationalHours,
      [day]: enabled
        ? {
            ...operationalHours[day],
            enabled: true,
            open: operationalHours[day]?.open || "06:00",
            close: operationalHours[day]?.close || "23:00",
          }
        : { enabled: false, open: "", close: "" },
    });
  };

  const setTime = (day, type, value) => {
    if (typeof onOperationalHoursChange !== "function") return;
    onOperationalHoursChange({
      ...operationalHours,
      [day]: { ...operationalHours[day], [type]: value },
    });
  };

  return (
    <Modal show={!!show} onHide={onClose} centered scrollable size={showOperationalHours ? "xl" : "lg"}>
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          overflowY: "auto",
          height: "auto",
          maxHeight: "calc(100vh - 12rem)",
        }}
      >
        <div className="row gx-3 gy-3">
          {fields.map((field) => (
            <div className="col-md-4" key={field.name}>
              <label htmlFor={`ship-${field.name}`} className="form-label fw-semibold small mb-1">
                {field.label}
                {field.required && <span className="text-danger">*</span>}
              </label>
              {field.type === "select" ? (
                <select
                  id={`ship-${field.name}`}
                  className={`form-select form-select-sm ${errors[field.name] ? "is-invalid" : ""}`}
                  value={form[field.name] || ""}
                  onChange={(e) => setField(field.name, e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Select {field.label}</option>
                  {(field.options || []).map((opt) => (
                    <option key={optionValue(opt)} value={optionValue(opt)}>
                      {optionLabel(opt)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`ship-${field.name}`}
                  type={field.type || "text"}
                  className={`form-control form-control-sm ${errors[field.name] ? "is-invalid" : ""}`}
                  value={form[field.name] || ""}
                  maxLength={field.maxLength}
                  onChange={(e) => setField(field.name, e.target.value)}
                  readOnly={readOnly}
                />
              )}
              {errors[field.name] && (
                <div className="invalid-feedback d-block">{errors[field.name]}</div>
              )}
            </div>
          ))}
        </div>
        {showOperationalHours && (
          <div className="border-top pt-3 mt-3">
            <h6 className="fw-bold mb-3">Operational Hours</h6>
            {errors.operationalHours && (
              <div className="text-danger small mb-2">{errors.operationalHours}</div>
            )}
            <div className="row gx-3 gy-3">
              {OPERATIONAL_DAYS.map((day) => (
                <div key={day} className="col-md-4">
                  <div className="d-flex flex-column border rounded p-2 bg-light">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`ship-hours-${day}`}
                        checked={operationalHours[day]?.enabled || false}
                        onChange={(e) => setDay(day, e.target.checked)}
                        disabled={readOnly}
                      />
                      <label className="form-check-label small" htmlFor={`ship-hours-${day}`}>
                        {day}
                      </label>
                    </div>
                    <div className="d-flex justify-content-between">
                      <input
                        type="time"
                        className="form-control form-control-sm me-1"
                        aria-label={`${day} open`}
                        value={operationalHours[day]?.open || "06:00"}
                        disabled={!operationalHours[day]?.enabled || readOnly}
                        onChange={(e) => setTime(day, "open", e.target.value)}
                      />
                      <input
                        type="time"
                        className="form-control form-control-sm ms-1"
                        aria-label={`${day} close`}
                        value={operationalHours[day]?.close || "23:00"}
                        disabled={!operationalHours[day]?.enabled || readOnly}
                        onChange={(e) => setTime(day, "close", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Close
        </button>
        {!readOnly && typeof onSave === "function" && (
          <button type="button" className="btn btn-primary" onClick={() => onSave(form)} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
